import { useEffect, useState } from "react";

import Bugsnag from "@bugsnag/js";
import { PrismicPreview } from "@prismicio/next";
import { PrismicProvider } from "@prismicio/react";
import { useReducedMotion } from "@react-spring/web";
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import axios, { AxiosError } from "axios";
import type { AppProps } from "next/app";
import localFont from "next/font/local";
import Head from "next/head";
import Link, { LinkProps } from "next/link";
import { useRouter } from "next/router";
import Script from "next/script";
import { env } from "next-runtime-env";

import AdminPanel from "components/adminPanel/AdminPanel";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import { setAdminQueryDefaults } from "queries/admin-queries";
import { setCartQueryDefaults } from "queries/cart-queries";
import { setHealthQueryDefaults } from "queries/health-queries";
import { setOrderQueryDefaults } from "queries/order-queries";
import { setProductQueryDefaults } from "queries/product-queries";
import { setWebsiteQueryDefaults } from "queries/website-queries";
import { pageview } from "tracking/gtm";
import { repositoryName, useReStylePrismicToolbar } from "utils/prismic-utils";

const atakFont = localFont({
  src: [
    {
      path: "./fonts/Atak-Regular.woff2",
      weight: "normal",
      style: "normal",
    },
    {
      path: "./fonts/Atak-Semibold.woff2",
      weight: "bold",
      style: "normal",
    },
  ],
});
const rzaFont = localFont({
  src: [
    {
      path: "./fonts/Rza-Regular.woff2",
      weight: "normal",
      style: "normal",
    },
    {
      path: "./fonts/Rza-Regular.woff2",
      weight: "bold",
      style: "normal",
    },
  ],
});

import "styles/globals.scss";

axios.defaults.timeout = Number(
  env("NEXT_PUBLIC_REQUEST_DEFAULT_TIMEOUT_MS") ?? 8000
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error instanceof AxiosError &&
      error.code === "ECONNABORTED" &&
      error.message.includes("timeout")
    ) {
      // In the event of a timeout, include info about the request in the error message.
      // This will be bubbled up to Bugsnag in the relevant handler.
      return Promise.reject(
        Error(
          `${
            error.config?.url
              ? `Request to ${error.config?.url}`
              : "Request without config"
          } timed out after ${
            env("NEXT_PUBLIC_REQUEST_DEFAULT_TIMEOUT_MS") || 8000
          }ms`
        )
      );
    }
    return Promise.reject(error);
  }
);

type CanvasProps = AppProps & {
  pageProps: {
    dehydratedState: unknown;
  };
};

// define here for type guard
const bugsnagAPIKey = env("NEXT_PUBLIC_BUGSNAG_API_KEY");

if (
  env("NEXT_PHASE") !== "phase-production-build" &&
  bugsnagAPIKey &&
  (env("NEXT_PUBLIC_CANVAS_ENV") === "staging" ||
    env("NEXT_PUBLIC_CANVAS_ENV") === "production") &&
  // Private field used to stop BugSnag getting initialised multiple times
  // https://github.com/bugsnag/bugsnag-js/issues/811
  // @ts-ignore
  !Bugsnag._client
) {
  Bugsnag.start({
    apiKey: bugsnagAPIKey,
    appVersion: env("NEXT_BUILD_ID"),
    plugins: [],
  });
}

function Canvas({ Component, pageProps }: CanvasProps) {
  const router = useRouter();

  /**
   * Make sure we don't run any `react-spring` animations for users
   * with `prefers-reduced-motion` set
   */
  useReducedMotion();

  useReStylePrismicToolbar();

  const [queryClient] = useState(() => {
    const queryClient = new QueryClient();

    // Set default options for our different queries.
    // This makes them easier to overwrite in tests.
    setProductQueryDefaults(queryClient);
    setHealthQueryDefaults(queryClient);
    setCartQueryDefaults(queryClient);
    setOrderQueryDefaults(queryClient);
    setWebsiteQueryDefaults(queryClient);
    setAdminQueryDefaults(queryClient);

    return queryClient;
  });

  /**
   * Listen for changes in router and track as pageviews for GTM
   */
  useEffect(() => {
    router.events.on("routeChangeComplete", pageview);

    return () => {
      router.events.off("routeChangeComplete", pageview);
    };
  }, [router.events]);

  return (
    <>
      {env("NEXT_PUBLIC_OPTIMIZELY_ID") && (
        <Script
          src={`https://cdn.optimizely.com/js/${env(
            "NEXT_PUBLIC_OPTIMIZELY_ID"
          )}.js`}
        />
      )}

      {/* eslint-disable-next-line react/no-unknown-property */}
      <style global jsx>{`
        html {
          --font-atak: ${atakFont?.style?.fontFamily};
          --font-rza: ${rzaFont?.style?.fontFamily};
        }
      `}</style>

      <Head>
        <meta
          content="viewport-fit=cover, width=device-width, initial-scale=1"
          name="viewport"
        />
        <link href="/_canvas/favicon.png" rel="icon" type="image/png" />
        <link
          href="/_canvas/apple-touch-icon-180x180.png"
          rel="apple-touch-icon"
        />
      </Head>

      <PrismicProvider
        internalLinkComponent={(props: LinkProps) => <Link {...props} />}
      >
        <PrismicPreview
          exitPreviewURL="/_canvas/api/exit-preview"
          repositoryName={repositoryName}
          updatePreviewURL="/_canvas/api/preview"
        >
          <QueryClientProvider client={queryClient}>
            <Hydrate state={pageProps.dehydratedState}>
              <ErrorBoundary>
                {(env("NEXT_PUBLIC_CANVAS_ENV") === "development" ||
                  env("NEXT_PUBLIC_CANVAS_ENV") === "staging" ||
                  env("NEXT_PUBLIC_CANVAS_ENV") === "pr_app") && <AdminPanel />}
                <Component {...pageProps} />
              </ErrorBoundary>
              <ReactQueryDevtools initialIsOpen={false} />
            </Hydrate>
          </QueryClientProvider>
        </PrismicPreview>
      </PrismicProvider>
    </>
  );
}

export default Canvas;
